// API Base URL - will be set at runtime from config.json
// Empty string means use relative paths (same origin as the web page)
let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/** Read the JWT access token stored by AuthContext after login. */
function getStoredToken() {
  return localStorage.getItem('access_token');
}

/**
 * Initialize API service with runtime configuration
 * This should be called once when the app loads with config from config.json
 */
export function initializeAPI(config) {
  if (config && config.API_BASE_URL) {
    API_BASE_URL = config.API_BASE_URL;
    console.log('API Service initialized with base URL:', API_BASE_URL);
  }
}

/**
 * Get current API base URL
 */
export function getAPIBaseURL() {
  return API_BASE_URL;
}

/**
 * Generic API request handler with authentication
 */
async function apiRequest(endpoint, options = {}) {
  try {
    // Get JWT token from localStorage (set by AuthContext on login)
    const token = getStoredToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...options.headers,
    };

    const config = {
      ...options,
      headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    const isJson = contentType && contentType.includes('application/json');

    if (!response.ok) {
      let errorMessage;
      let errorData;

      try {
        errorData = isJson ? await response.json() : { message: response.statusText };
      } catch (parseError) {
        errorData = { message: response.statusText || 'Unknown error' };
      }

      // Extract meaningful error message
      if (errorData.detail) {
        errorMessage = typeof errorData.detail === 'string'
          ? errorData.detail
          : JSON.stringify(errorData.detail);
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else {
        errorMessage = `HTTP ${response.status}: ${response.statusText || 'Request failed'}`;
      }

      // Add context for common HTTP errors
      if (response.status === 503) {
        errorMessage = `Service unavailable: ${errorMessage}. The backend API may be down or unreachable.`;
      } else if (response.status === 401) {
        errorMessage = `Authentication failed: ${errorMessage}`;
      } else if (response.status === 403) {
        errorMessage = `Access denied: ${errorMessage}`;
      } else if (response.status === 404) {
        errorMessage = `Not found: ${errorMessage}`;
      } else if (response.status === 500) {
        errorMessage = `Server error: ${errorMessage}`;
      }

      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
        message: errorMessage
      });

      throw new Error(errorMessage);
    }

    return isJson ? await response.json() : await response.text();
  } catch (error) {
    console.error('API Request Error:', error);
    throw error;
  }
}

// =========================
// EMPLOYEE APIS
// =========================

export const employeeAPI = {
  /**
   * Create new employee
   */
  create: async (employeeData) => {
    return apiRequest('/api/employee/create', {
      method: 'POST',
      body: JSON.stringify(employeeData),
    });
  },

  /**
   * Get the profile of the currently logged-in employee (matched by email)
   */
  getMe: async () => {
    return apiRequest('/api/employee/me', { method: 'GET' });
  },

  /**
   * Update employee profile
   */
  update: async (employeeId, profileData) => {
    return apiRequest(`/api/employee/${employeeId}/profile`, {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  /**
   * Get employee by ID
   */
  getById: async (employeeId) => {
    return apiRequest(`/api/employee/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Get all employees
   */
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/employee?${queryString}` : '/api/employee';
    return apiRequest(endpoint, {
      method: 'GET',
    });
  },

  /**
   * Get employees by establishment
   */
  getByEstablishment: async (establishmentId) => {
    return apiRequest(`/api/employee?establishment_id=${establishmentId}`, {
      method: 'GET',
    });
  },

  /**
   * Delete employee
   */
  delete: async (employeeId) => {
    return apiRequest(`/api/employee/${employeeId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Check if a field value is unique
   * @param {string} field - Field name (pan, aadhar, esicIp, uan, mobile, emergencyContactNo, email, accountNumber)
   * @param {string} value - Field value to check
   * @param {string} excludeEmployeeId - Optional employee ID to exclude (for updates)
   */
  checkUnique: async (field, value, excludeEmployeeId = null) => {
    return apiRequest('/api/employee/check-unique', {
      method: 'POST',
      body: JSON.stringify({
        field,
        value,
        exclude_employee_id: excludeEmployeeId
      }),
    });
  },

  /**
   * Upload attachment for employee (photos, documents, certificates)
   * @param {File} file - File to upload
   * @param {string} employeeId - Employee ID
   * @param {string} category - Optional category (photo, document, certificate)
   */
  uploadAttachment: async (file, employeeId, category = null) => {
    const token = getStoredToken();

    const formData = new FormData();
    formData.append('file', file);
    if (category) {
      formData.append('category', category);
    }

    const response = await fetch(`${API_BASE_URL}/api/employee/${employeeId}/upload-attachment`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload attachment');
    }

    return await response.json();
  },

  /**
   * Get all attachments for an employee
   * @param {string} employeeId - Employee ID
   */
  getAttachments: async (employeeId) => {
    return apiRequest(`/api/employee/${employeeId}/attachments`, {
      method: 'GET',
    });
  },

  /**
   * Delete an employee attachment
   * @param {string} employeeId - Employee ID
   * @param {number} attachmentId - Attachment ID
   */
  deleteAttachment: async (employeeId, attachmentId) => {
    return apiRequest(`/api/employee/${employeeId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  },
};

// =========================
// ESTABLISHMENT APIS
// =========================

export const establishmentAPI = {
  /**
   * Create new establishment
   */
  create: async (establishmentData) => {
    return apiRequest('/api/establishment/create', {
      method: 'POST',
      body: JSON.stringify(establishmentData),
    });
  },

  /**
   * Update establishment
   */
  update: async (establishmentId, establishmentData) => {
    return apiRequest(`/api/establishment/${establishmentId}`, {
      method: 'PUT',
      body: JSON.stringify(establishmentData),
    });
  },

  /**
   * Get all establishments (includes full details for each establishment)
   */
  getAll: async () => {
    return apiRequest('/api/establishment', {
      method: 'GET',
    });
  },

  /**
   * Get departments, optionally filtered by establishment
   */
  getDepartments: async (establishmentId = null) => {
    const endpoint = establishmentId
      ? `/api/establishment/departments?establishment_id=${establishmentId}`
      : '/api/establishment/departments';
    return apiRequest(endpoint, {
      method: 'GET',
    });
  },

  /**
   * Delete establishment (soft delete)
   */
  delete: async (establishmentId) => {
    return apiRequest(`/api/establishment/${establishmentId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Check if a field value is unique
   * @param {string} field - Field name (code, gstin, pan, esicCode, epfCode, bankAccountNumber, mainEmail, registeredEmail)
   * @param {string} value - Field value to check
   * @param {string} excludeEstablishmentId - Optional establishment ID to exclude (for updates)
   */
  checkUnique: async (field, value, excludeEstablishmentId = null) => {
    return apiRequest('/api/establishment/check-unique', {
      method: 'POST',
      body: JSON.stringify({
        field,
        value,
        exclude_establishment_id: excludeEstablishmentId
      }),
    });
  },

  /**
   * Upload attachment for establishment (logos, images, documents)
   * @param {File} file - File to upload
   * @param {number} establishmentId - Establishment ID
   * @param {string} category - Optional category (logo, signature)
   */
  uploadAttachment: async (file, establishmentId, category = null) => {
    const token = getStoredToken();

    const formData = new FormData();
    formData.append('file', file);
    if (category) {
      formData.append('category', category);
    }

    const response = await fetch(`${API_BASE_URL}/api/establishment/${establishmentId}/upload-attachment`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload attachment');
    }

    return await response.json();
  },

  /**
   * Get all attachments for an establishment
   * @param {number} establishmentId - Establishment ID
   */
  getAttachments: async (establishmentId) => {
    return apiRequest(`/api/establishment/${establishmentId}/attachments`, {
      method: 'GET',
    });
  },

  /**
   * Delete an establishment attachment
   * @param {number} establishmentId - Establishment ID
   * @param {number} attachmentId - Attachment ID
   */
  deleteAttachment: async (establishmentId, attachmentId) => {
    return apiRequest(`/api/establishment/${establishmentId}/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  },
};

// =========================
// LEAVE APIS
// =========================

export const leaveAPI = {
  /**
   * Apply for leave
   */
  apply: async (leaveData) => {
    return apiRequest('/api/leave/apply', {
      method: 'POST',
      body: JSON.stringify(leaveData),
    });
  },

  /**
   * Approve or reject leave
   */
  approve: async (leaveId, approvalData) => {
    return apiRequest(`/api/leave/${leaveId}/approve`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  },

  /**
   * Get employee leaves
   */
  getByEmployee: async (employeeId) => {
    return apiRequest(`/api/leave/employee/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Get pending leave requests
   */
  getPending: async () => {
    return apiRequest('/api/leave/pending', {
      method: 'GET',
    });
  },

  /**
   * Get leave balance
   */
  getBalance: async (employeeId) => {
    return apiRequest(`/api/attendance/leave-balance/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Get leave balance (new endpoint)
   */
  getLeaveBalance: async (employeeId) => {
    return apiRequest(`/api/leave/balance/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Record leave/absent for multiple employees
   */
  recordLeaveAbsent: async (data) => {
    return apiRequest('/api/leave/record-absent', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// =========================
// ANALYTICS APIS
// =========================
export const analyticsAPI = {
  getOverview: async (establishmentId = null, months = 12) => {
    const params = new URLSearchParams();
    if (establishmentId) params.append('establishment_id', establishmentId);
    params.append('months', months);
    return apiRequest(`/api/analytics/overview?${params}`, { method: 'GET' });
  },
};

// =========================
// RESIGNATION APIS
// =========================
export const resignationAPI = {
  apply: async (data) =>
    apiRequest('/api/resignation/apply', { method: 'POST', body: JSON.stringify(data) }),

  approve: async (requestId, approvalData) =>
    apiRequest(`/api/resignation/${requestId}/approve`, { method: 'PUT', body: JSON.stringify(approvalData) }),

  adminTerminate: async (data) =>
    apiRequest('/api/resignation/admin-terminate', { method: 'POST', body: JSON.stringify(data) }),

  getPending: async (establishmentId = null) => {
    const qs = establishmentId ? `?establishment_id=${establishmentId}` : '';
    return apiRequest(`/api/resignation/pending${qs}`, { method: 'GET' });
  },

  getByEmployee: async (employeeId) =>
    apiRequest(`/api/resignation/employee/${employeeId}`, { method: 'GET' }),
};

// =========================
// LEAVE CONFIG APIS
// =========================

export const leaveConfigAPI = {
  /**
   * Get leave configuration for an employee
   */
  getConfig: async (employeeId) => {
    return apiRequest(`/api/leave-config/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Create or update leave configuration
   */
  saveConfig: async (employeeId, configData) => {
    return apiRequest(`/api/leave-config/${employeeId}`, {
      method: 'POST',
      body: JSON.stringify(configData),
    });
  },

  /**
   * Get current leave balance (considering probation)
   */
  getBalance: async (employeeId) => {
    return apiRequest(`/api/leave-config/balance/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Trigger monthly leave accrual
   */
  accrueLeaves: async (employeeId) => {
    return apiRequest(`/api/leave-config/accrue/${employeeId}`, {
      method: 'POST',
    });
  },
};

// =========================
// WAGES APIS
// =========================

export const wagesAPI = {
  /**
   * Add wages record
   */
  add: async (wagesData) => {
    return apiRequest('/api/wages/add', {
      method: 'POST',
      body: JSON.stringify(wagesData),
    });
  },

  /**
   * Get employee wages
   */
  getByEmployee: async (employeeId) => {
    return apiRequest(`/api/wages/employee/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Add bonus/allowance
   */
  addBonus: async (bonusData) => {
    return apiRequest('/api/wages/bonus-allowances', {
      method: 'POST',
      body: JSON.stringify(bonusData),
    });
  },

  /**
   * Add loan/advance
   */
  addLoan: async (loanData) => {
    return apiRequest('/api/wages/loans-advances', {
      method: 'POST',
      body: JSON.stringify(loanData),
    });
  },

  /**
   * Add recovery
   */
  addRecovery: async (recoveryData) => {
    return apiRequest('/api/wages/recoveries', {
      method: 'POST',
      body: JSON.stringify(recoveryData),
    });
  },

  /**
   * Add variable pay
   */
  addVariablePay: async (variablePayData) => {
    return apiRequest('/api/wages/variable-pay', {
      method: 'POST',
      body: JSON.stringify(variablePayData),
    });
  },

  /**
   * Add damage/loss record
   */
  addDamage: async (damageData) => {
    return apiRequest('/api/wages/damages', {
      method: 'POST',
      body: JSON.stringify(damageData),
    });
  },

  /**
   * Get bonuses/allowances
   */
  getBonuses: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/bonus-allowances?${queryString}` : '/api/wages/bonus-allowances';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get loans/advances
   */
  getLoans: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/loans-advances?${queryString}` : '/api/wages/loans-advances';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get recoveries/deductions
   */
  getRecoveries: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/recoveries?${queryString}` : '/api/wages/recoveries';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get damages/losses
   */
  getDamages: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/damages?${queryString}` : '/api/wages/damages';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Delete bonus/allowance (archive)
   */
  deleteBonus: async (id) => {
    return apiRequest(`/api/wages/bonus-allowances/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete loan/advance (archive)
   */
  deleteLoan: async (id) => {
    return apiRequest(`/api/wages/loans-advances/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete recovery (archive)
   */
  deleteRecovery: async (id) => {
    return apiRequest(`/api/wages/recoveries/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete variable pay (archive)
   */
  deleteVariablePay: async (id) => {
    return apiRequest(`/api/wages/variable-pay/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete damage/loss (archive)
   */
  deleteDamage: async (id) => {
    return apiRequest(`/api/wages/damages/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add other deduction
   */
  addOtherDeduction: async (deductionData) => {
    return apiRequest('/api/wages/other-deductions', {
      method: 'POST',
      body: JSON.stringify(deductionData),
    });
  },

  /**
   * Get other deductions
   */
  getOtherDeductions: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/other-deductions?${queryString}` : '/api/wages/other-deductions';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Delete other deduction
   */
  deleteOtherDeduction: async (id) => {
    return apiRequest(`/api/wages/other-deductions/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Add other allowance
   */
  addOtherAllowance: async (allowanceData) => {
    return apiRequest('/api/wages/other-allowances', {
      method: 'POST',
      body: JSON.stringify(allowanceData),
    });
  },

  /**
   * Get other allowances
   */
  getOtherAllowances: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/wages/other-allowances?${queryString}` : '/api/wages/other-allowances';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Delete other allowance
   */
  deleteOtherAllowance: async (id) => {
    return apiRequest(`/api/wages/other-allowances/${id}`, {
      method: 'DELETE',
    });
  },
};

// =========================
// ATTENDANCE APIS
// =========================

export const attendanceAPI = {
  /**
   * Record overtime
   */
  addOvertime: async (overtimeData) => {
    return apiRequest('/api/attendance/overtime', {
      method: 'POST',
      body: JSON.stringify(overtimeData),
    });
  },

  /**
   * Create/Update shift schedule
   */
  updateShiftSchedule: async (shiftData) => {
    return apiRequest('/api/attendance/shift-schedule', {
      method: 'POST',
      body: JSON.stringify(shiftData),
    });
  },

  /**
   * Get shift schedules
   */
  getShiftSchedules: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/attendance/shifts?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Add holidays
   */
  addHolidays: async (holidayData) => {
    return apiRequest('/api/attendance/holidays', {
      method: 'POST',
      body: JSON.stringify(holidayData),
    });
  },

  /**
   * Get holidays by year
   */
  getHolidays: async (year) => {
    return apiRequest(`/api/attendance/holidays/${year}`, {
      method: 'GET',
    });
  },

  /**
   * Record weekly attendance
   */
  recordWeeklyAttendance: async (attendanceData) => {
    return apiRequest('/api/attendance/weekly', {
      method: 'POST',
      body: JSON.stringify(attendanceData),
    });
  },

  /**
   * Get weekly attendance records
   */
  getWeeklyAttendance: async (employeeId, weekStart = null) => {
    const params = new URLSearchParams({ employee_id: employeeId });
    if (weekStart) {
      params.append('week_start', weekStart);
    }
    return apiRequest(`/api/attendance/weekly?${params.toString()}`, {
      method: 'GET',
    });
  },

  /**
   * Record absence
   */
  recordAbsence: async (absenceData) => {
    return apiRequest('/api/attendance/absence', {
      method: 'POST',
      body: JSON.stringify(absenceData),
    });
  },

  /**
   * Get absence history
   */
  getAbsenceHistory: async (employeeId) => {
    return apiRequest(`/api/attendance/absence/history/${employeeId}`, {
      method: 'GET',
    });
  },

  /**
   * Get overtime records
   */
  getOvertimeRecords: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/attendance/overtime?${queryString}` : '/api/attendance/overtime';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get overtime summary for an employee
   */
  getOvertimeSummary: async (employeeId, month = null) => {
    const endpoint = month
      ? `/api/attendance/overtime/summary/${employeeId}?month=${month}`
      : `/api/attendance/overtime/summary/${employeeId}`;
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Delete overtime record
   */
  deleteOvertime: async (id) => {
    return apiRequest(`/api/attendance/overtime/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Delete shift schedule
   */
  deleteShift: async (id) => {
    return apiRequest(`/api/attendance/shift-schedule/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Download pre-filled attendance template .xlsx
   */
  downloadTemplate: async (establishmentId, month, year) => {
    const token = getStoredToken();
    const response = await fetch(
      `${API_BASE_URL}/api/attendance/download-template?establishment_id=${establishmentId}&month=${month}&year=${year}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Download failed');
    }
    return response.blob();
  },

  /**
   * Upload fixed-format attendance spreadsheet for deterministic parsing
   */
  uploadSpreadsheet: async (file, establishmentId, month, year, weekInfo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    if (weekInfo) {
      formData.append('week', weekInfo.week);
      formData.append('day_start', weekInfo.dayStart);
      formData.append('day_end', weekInfo.dayEnd);
    }
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/upload-spreadsheet`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Upload failed');
    }
    return response.json();
  },

  /**
   * Upload attendance image for AI vision parsing
   */
  uploadAttendanceImage: async (file, establishmentId, month, year, weekInfo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    if (weekInfo) {
      formData.append('week', weekInfo.week);
      formData.append('day_start', weekInfo.dayStart);
      formData.append('day_end', weekInfo.dayEnd);
    }
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/upload-attendance-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Image upload failed');
    }
    return response.json();
  },

  /**
   * Upload attendance image/PDF for PaddleOCR (local) parsing.
   * Reads each day cell's mark; unreadable cells come back as status
   * "needs_review" for manual correction before saving.
   */
  uploadAttendanceImagePaddle: async (file, establishmentId, month, year, weekInfo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    if (weekInfo) {
      formData.append('week', weekInfo.week);
      formData.append('day_start', weekInfo.dayStart);
      formData.append('day_end', weekInfo.dayEnd);
    }
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/upload-attendance-image-paddle`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'PaddleOCR image upload failed');
    }
    return response.json();
  },

  /**
   * Upload an arbitrary vendor/client attendance sheet for Claude vision extraction.
   * Handles unknown layouts + handwriting; returns per-day records for review.
   */
  uploadAttendanceImageVision: async (file, establishmentId, month, year, weekInfo) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    if (weekInfo) {
      formData.append('week', weekInfo.week);
      formData.append('day_start', weekInfo.dayStart);
      formData.append('day_end', weekInfo.dayEnd);
    }
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/upload-attendance-image-vision`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Claude vision extraction failed');
    }
    return response.json();
  },

  /**
   * Upload shift schedule image/PDF for AI parsing
   */
  uploadShiftImage: async (file, establishmentId, month, year) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/upload-shift-image`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Shift image upload failed');
    }
    return response.json();
  },

  /**
   * Confirm and save parsed spreadsheet records
   */
  confirmSpreadsheet: async (records, establishmentId, month, year) => {
    return apiRequest('/api/attendance/confirm-spreadsheet-upload', {
      method: 'POST',
      body: JSON.stringify({ records, establishment_id: establishmentId, month, year }),
    });
  },

  /**
   * Download shift schedule template
   */
  downloadShiftTemplate: async (establishmentId, month, year) => {
    const token = getStoredToken();
    const response = await fetch(
      `${API_BASE_URL}/api/attendance/shift-schedule/download-template?establishment_id=${establishmentId}&month=${month}&year=${year}`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );
    if (!response.ok) throw new Error('Failed to download shift template');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shift_schedule_${String(month).padStart(2,'0')}_${year}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Upload shift schedule spreadsheet for parsing
   */
  uploadShiftSpreadsheet: async (file, establishmentId, month, year) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('establishment_id', establishmentId);
    formData.append('month', month);
    formData.append('year', year);
    const token = getStoredToken();
    const response = await fetch(`${API_BASE_URL}/api/attendance/shift-schedule/upload-spreadsheet`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.detail || 'Shift spreadsheet upload failed');
    }
    return response.json();
  },

  /**
   * Confirm and save parsed shift spreadsheet records
   */
  confirmShiftSpreadsheet: async (records, establishmentId, month, year) => {
    return apiRequest('/api/attendance/shift-schedule/confirm-spreadsheet', {
      method: 'POST',
      body: JSON.stringify({ records, establishment_id: establishmentId, month, year }),
    });
  },

  /**
   * Get scanner-based attendance data for a month
   */
  getScannerAttendance: async (establishmentId, month, year) => {
    return apiRequest(
      `/api/attendance/scanner-attendance/${establishmentId}?month=${month}&year=${year}`,
      { method: 'GET' }
    );
  },

  /**
   * Sync scanner attendance into work_days_tracking
   */
  syncScannerAttendance: async (establishmentId, month, year, records) => {
    return apiRequest('/api/attendance/sync-scanner-attendance', {
      method: 'POST',
      body: JSON.stringify({ establishment_id: establishmentId, month, year, records }),
    });
  },

  /**
   * Delete holiday
   */
  deleteHoliday: async (id) => {
    return apiRequest(`/api/attendance/holidays/${id}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get grid attendance data for monthly view
   * Returns employees, leave balances, attendance records for the month
   */
  getGridData: async (establishmentId, month, year) => {
    return apiRequest(
      `/api/attendance/grid-data/${establishmentId}?month=${month}&year=${year}`,
      { method: 'GET' }
    );
  },

  /**
   * Save grid attendance changes (batch)
   * Handles leave balance deductions
   */
  saveGridChanges: async (data) => {
    return apiRequest('/api/attendance/grid-save', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  resyncWeeklyOffs: async (data) => {
    return apiRequest('/api/attendance/resync-weekly-offs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Member self-service: mark own attendance for today or a past date.
   * employee_id is derived from the JWT on the backend.
   */
  markMyAttendance: async (payload) => {
    return apiRequest('/api/attendance/my-mark', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Member self-service: own attendance history (default last 30 days).
   */
  getMyHistory: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== ''))
    ).toString();
    return apiRequest(`/api/attendance/my-history${qs ? '?' + qs : ''}`, { method: 'GET' });
  },

  /**
   * Member self-service: full month dashboard (per-day check-in/out, holidays,
   * week offs, leaves, summary counters).
   */
  getMyMonthlySummary: async (month, year) => {
    return apiRequest(`/api/attendance/my-monthly-summary?month=${month}&year=${year}`, { method: 'GET' });
  },
};

// =========================
// CONTRACTOR APIS
// =========================

export const contractorAPI = {
  /**
   * Create contractor
   */
  create: async (contractorData) => {
    return apiRequest('/api/contractor/create', {
      method: 'POST',
      body: JSON.stringify(contractorData),
    });
  },

  /**
   * Get all contractors
   */
  getAll: async () => {
    return apiRequest('/api/contractors', {
      method: 'GET',
    });
  },
};

// =========================
// REPORTS APIS
// =========================

export const reportsAPI = {
  /**
   * Get salary report
   */
  getSalaryReport: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/reports/salary?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get leave report
   */
  getLeaveReport: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/reports/leave?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get employment statistics
   */
  getEmploymentStats: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/reports/employment-statistics?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get form data for statutory forms
   */
  getFormData: async (formType, params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/reports/form-${formType}?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get Monthly Information Services (MIS) data
   * Returns: newJoinees, relieved, totalWorkingHours, turnoverRate
   */
  getMIS: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/reports/mis?${queryString}`, {
      method: 'GET',
    });
  },
};

// =========================
// PAYROLL APIS
// =========================

export const payrollAPI = {
  /**
   * Calculate monthly payroll
   */
  calculateMonthly: async (data) => {
    return apiRequest('/api/payroll/calculate-monthly', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  /**
   * Get monthly payroll data
   */
  getMonthly: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/payroll/monthly?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get payroll summary
   */
  getSummary: async (establishmentId, year = null) => {
    const endpoint = year
      ? `/api/payroll/summary/${establishmentId}?year=${year}`
      : `/api/payroll/summary/${establishmentId}`;
    return apiRequest(endpoint, { method: 'GET' });
  },
};

// =========================
// FORMS APIS
// =========================

export const formsAPI = {
  /**
   * Generate individual employee form (UNIFIED ENDPOINT)
   * Uses POST /api/forms/generate for all form types
   */
  generateForm: async (formType, params) => {
    const queryString = new URLSearchParams({ form_type: formType, ...params }).toString();
    return apiRequest(`/api/forms/generate?${queryString}`, {
      method: 'POST',
    });
  },

  /**
   * Generate bulk forms for all employees (UNIFIED ENDPOINT)
   * Uses POST /api/forms/generate without employee_id for bulk generation
   */
  generateBulkForms: async (formType, params) => {
    const queryString = new URLSearchParams({ form_type: formType, ...params }).toString();
    return apiRequest(`/api/forms/generate?${queryString}`, {
      method: 'POST',
    });
  },

  /**
   * Generate all forms for establishment
   */
  generateAllForms: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/generate_all_forms?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get Form V - Festival Holidays (establishment-level)
   */
  getFormVHolidays: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/form_v_holidays?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Download Form V - Festival Holidays
   */
  downloadFormVHolidays: async (establishmentId, year) => {
    return apiRequest(`/api/forms/form_v_holidays/download?establishment_id=${establishmentId}&year=${year}`, {
      method: 'GET',
    });
  },

  /**
   * Get Form U - Annual Return (establishment-level)
   */
  getFormUAnnual: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/form_u_annual?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * List generated forms with filters
   */
  listGeneratedForms: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/list?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Get forms summary statistics
   */
  getFormsSummary: async (params) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/summary?${queryString}`, {
      method: 'GET',
    });
  },

  /**
   * Delete a generated form
   */
  deleteForm: async (formId) => {
    return apiRequest(`/api/forms/${formId}/delete`, {
      method: 'DELETE',
    });
  },

  /**
   * Validate all generated forms
   */
  validateAllForms: async () => {
    return apiRequest('/api/forms/validate_all', {
      method: 'GET',
    });
  },

  /**
   * Validate a single form file
   */
  validateForm: async (filename) => {
    return apiRequest(`/api/forms/validate_file/${filename}`, {
      method: 'GET',
    });
  },

  /**
   * Get list of all generated forms with optional filters
   */
  getList: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiRequest(`/api/forms/list${queryString ? '?' + queryString : ''}`, {
      method: 'GET',
    });
  },

  /**
   * Get summary statistics for generated forms
   */
  getSummary: async (establishmentId = null) => {
    const params = establishmentId ? `?establishment_id=${establishmentId}` : '';
    return apiRequest(`/api/forms/summary${params}`, {
      method: 'GET',
    });
  },

  /**
   * Delete a generated form (soft delete)
   */
  delete: async (formId) => {
    return apiRequest(`/api/forms/${formId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Download a generated form by its ID
   */
  downloadById: async (formId) => {
    const response = await fetch(`${API_BASE_URL}/api/forms/download_by_id/${formId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${getStoredToken()}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download form');
    }

    return response.blob();
  },
};

// =========================
// INVOICE APIS
// =========================

export const invoiceAPI = {
  /**
   * Get establishments for invoice generation
   */
  getEstablishments: async () => {
    return apiRequest('/api/invoice/establishments', { method: 'GET' });
  },

  /**
   * Generate a new invoice
   */
  generate: async (invoiceData) => {
    return apiRequest('/api/invoice/generate', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
  },

  /**
   * Get list of invoices with filters
   */
  getList: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const endpoint = queryString ? `/api/invoice/list?${queryString}` : '/api/invoice/list';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get invoice details by ID
   */
  getById: async (invoiceId) => {
    return apiRequest(`/api/invoice/${invoiceId}`, { method: 'GET' });
  },

  /**
   * Update invoice status
   */
  updateStatus: async (invoiceId, statusData) => {
    return apiRequest(`/api/invoice/${invoiceId}`, {
      method: 'PUT',
      body: JSON.stringify(statusData),
    });
  },

  /**
   * Record payment for an invoice
   */
  recordPayment: async (paymentData) => {
    return apiRequest('/api/invoice/payment', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  },

  /**
   * Delete an invoice
   */
  delete: async (invoiceId) => {
    return apiRequest(`/api/invoice/${invoiceId}`, { method: 'DELETE' });
  },

  /**
   * Get invoice summary statistics
   */
  getSummary: async (establishmentId = null) => {
    const endpoint = establishmentId
      ? `/api/invoice/stats/summary?establishment_id=${establishmentId}`
      : '/api/invoice/stats/summary';
    return apiRequest(endpoint, { method: 'GET' });
  },

  /**
   * Get invoice PDF - returns blob URL for preview/download
   */
  getPDF: async (invoiceId) => {
    const token = getStoredToken();

    const response = await fetch(`${API_BASE_URL}/api/invoice/${invoiceId}/pdf`, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate PDF: ${response.status} ${errorText}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Upload invoice attachment to S3
   */
  uploadAttachment: async (file, establishmentId) => {
    const token = getStoredToken();

    const formData = new FormData();
    formData.append('file', file);
    if (establishmentId) {
      formData.append('establishment_id', establishmentId);
    }

    const response = await fetch(`${API_BASE_URL}/api/invoice/upload-attachment?establishment_id=${establishmentId}`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Failed to upload attachment');
    }

    return await response.json();
  },

  /**
   * Download all invoice files (PDF + attachments) as ZIP
   */
  downloadAllFiles: async (invoiceId, attachmentKeys) => {
    const token = getStoredToken();

    const response = await fetch(`${API_BASE_URL}/api/invoice/download-all-files`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify({
        invoice_id: invoiceId,
        attachment_keys: attachmentKeys
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to download files: ${response.status} ${errorText}`);
    }

    const blob = await response.blob();
    return blob;
  },

  /**
   * List S3 files for an establishment
   */
  listS3Files: async (establishmentId) => {
    return apiRequest(`/api/invoice/s3-files/${establishmentId}`, {
      method: 'GET',
    });
  },
};

// Asset Management API
export const assetAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.establishment_id) queryParams.append('establishment_id', params.establishment_id);
    if (params.status) queryParams.append('status', params.status);
    if (params.asset_type) queryParams.append('asset_type', params.asset_type);
    if (params.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    const endpoint = `/api/assets${queryString ? `?${queryString}` : ''}`;
    return apiRequest(endpoint, { method: 'GET' });
  },

  getById: async (id) => {
    return apiRequest(`/api/assets/${id}`, { method: 'GET' });
  },

  getStats: async (establishmentId = null) => {
    const queryString = establishmentId ? `?establishment_id=${establishmentId}` : '';
    return apiRequest(`/api/assets/stats${queryString}`, { method: 'GET' });
  },

  getTypes: async () => {
    return apiRequest('/api/assets/types', { method: 'GET' });
  },

  create: async (data) => {
    return apiRequest('/api/assets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id, data) => {
    return apiRequest(`/api/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id) => {
    return apiRequest(`/api/assets/${id}`, { method: 'DELETE' });
  },

  assign: async (id, assignedToName, assignedToId = null) => {
    const params = new URLSearchParams();
    params.append('assigned_to_name', assignedToName);
    if (assignedToId) params.append('assigned_to_id', assignedToId);
    return apiRequest(`/api/assets/${id}/assign?${params.toString()}`, { method: 'POST' });
  },

  unassign: async (id) => {
    return apiRequest(`/api/assets/${id}/unassign`, { method: 'POST' });
  },
};

// Document Management API
export const documentAPI = {
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.category) queryParams.append('category', params.category);
    if (params.search) queryParams.append('search', params.search);
    const queryString = queryParams.toString();
    const endpoint = `/api/documents${queryString ? `?${queryString}` : ''}`;
    return apiRequest(endpoint, { method: 'GET' });
  },

  getById: async (id) => {
    return apiRequest(`/api/documents/${id}`, { method: 'GET' });
  },

  upload: async (file, metadata) => {
    const token = getStoredToken();

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', metadata.title);
    formData.append('category', metadata.category);
    if (metadata.description) formData.append('description', metadata.description);
    if (metadata.version) formData.append('version', metadata.version);

    const response = await fetch(`${API_BASE_URL}/api/documents/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || 'Upload failed');
    }

    return response.json();
  },

  getDownloadUrl: async (id) => {
    return apiRequest(`/api/documents/${id}/download-url`, { method: 'GET' });
  },

  delete: async (id) => {
    return apiRequest(`/api/documents/${id}`, { method: 'DELETE' });
  },

  getStats: async () => {
    return apiRequest('/api/documents/stats', { method: 'GET' });
  },

  // Establishment-specific document APIs
  getEstablishmentDocuments: async (establishmentId) => {
    return apiRequest(`/api/documents/establishment/${establishmentId}`, {
      method: 'GET',
    });
  },

  uploadEstablishmentDocument: async (establishmentId, file, description = '', category = 'general') => {
    const token = getStoredToken();

    const formData = new FormData();
    formData.append('file', file);

    const queryParams = new URLSearchParams();
    if (description) queryParams.append('description', description);
    if (category) queryParams.append('category', category);

    const response = await fetch(
      `${API_BASE_URL}/api/documents/establishment/${establishmentId}/upload?${queryParams.toString()}`,
      {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.detail || errorData.message || 'Upload failed');
    }

    return response.json();
  },

  deleteEstablishmentDocument: async (establishmentId, filePath) => {
    return apiRequest(`/api/documents/establishment/${establishmentId}/${encodeURIComponent(filePath)}`, {
      method: 'DELETE',
    });
  },
};

export const usersAPI = {
  list: () => apiRequest('/api/auth/users'),
  invite: (email, role, establishment_id = null, username = null, password = null, employee_id = null) => apiRequest('/api/auth/invite', {
    method: 'POST',
    body: JSON.stringify({ email, role, establishment_id, username, password, employee_id }),
  }),
  update: (userId, data) => apiRequest(`/api/auth/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
  approve: (userId) => apiRequest(`/api/auth/users/${userId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status: 'ACTIVE' }),
  }),
  delete: (userId) => apiRequest(`/api/auth/users/${userId}`, { method: 'DELETE' }),
  getUserEstablishments: (userId) => apiRequest(`/api/auth/users/${userId}/establishments`),
  assignEstablishment: (userId, establishmentId) => apiRequest(`/api/auth/users/${userId}/establishments`, {
    method: 'POST',
    body: JSON.stringify({ establishment_id: establishmentId }),
  }),
  removeEstablishment: (userId, establishmentId) => apiRequest(`/api/auth/users/${userId}/establishments/${establishmentId}`, {
    method: 'DELETE',
  }),
};

// =========================
// BARCODE ATTENDANCE APIS
// =========================

export const barcodeAPI = {
  /**
   * Scan a barcode — returns CHECKIN or CHECKOUT result.
   * @param {string} barcode
   * @param {number|null} establishment_id
   */
  scan: async (barcode, establishment_id = null) => {
    return apiRequest('/api/barcode/scan', {
      method: 'POST',
      body: JSON.stringify({ barcode, establishment_id }),
    });
  },

  /**
   * Get (or auto-generate) the logged-in employee's barcode + today's scan history.
   */
  myBarcode: async () => {
    return apiRequest('/api/barcode/my-barcode', { method: 'GET' });
  },

  /**
   * Today's live scan log for admin/manager view.
   * @param {number|null} establishment_id
   */
  today: async (establishment_id = null) => {
    const params = establishment_id ? `?establishment_id=${establishment_id}` : '';
    return apiRequest(`/api/barcode/today${params}`, { method: 'GET' });
  },

  /**
   * Paginated scan history.
   * @param {object} params — { employee_id, from_date, to_date, limit, offset }
   */
  history: async (params = {}) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null))
    ).toString();
    return apiRequest(`/api/barcode/history${qs ? '?' + qs : ''}`, { method: 'GET' });
  },

  /**
   * Get or generate barcode for a specific employee (Admin/Manager).
   */
  getEmployeeBarcode: async (employeeId) => {
    return apiRequest(`/api/barcode/employee/${employeeId}/barcode`, { method: 'GET' });
  },

  /**
   * Regenerate (invalidate old) barcode for an employee (Admin only).
   */
  regenerateBarcode: async (employeeId) => {
    return apiRequest(`/api/barcode/employee/${employeeId}/regenerate`, { method: 'POST' });
  },
};

// =========================
// GROUPS API
// =========================

export const groupsAPI = {
  /** List all groups with member counts */
  listGroups: async () => apiRequest('/api/groups', { method: 'GET' }),

  /** List members of a group */
  getGroupMembers: async (groupId) => apiRequest(`/api/groups/${groupId}/members`, { method: 'GET' }),

  /** Add a user to a group */
  addMember: async (groupId, userId) =>
    apiRequest(`/api/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  /** Remove a user from a group */
  removeMember: async (groupId, userId) =>
    apiRequest(`/api/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),

  /** List establishment assignments for a MultiClientAdmin user */
  getUserEstablishments: async (userId) =>
    apiRequest(`/api/auth/users/${userId}/establishments`, { method: 'GET' }),

  /** Assign an establishment to a MultiClientAdmin user */
  assignEstablishment: async (userId, establishmentId) =>
    apiRequest(`/api/auth/users/${userId}/establishments`, {
      method: 'POST',
      body: JSON.stringify({ establishment_id: establishmentId }),
    }),

  /** Remove an establishment assignment from a MultiClientAdmin user */
  removeEstablishment: async (userId, establishmentId) =>
    apiRequest(`/api/auth/users/${userId}/establishments/${establishmentId}`, { method: 'DELETE' }),
};

// =========================
// HR HELPDESK API
// =========================

// =========================
// HR ASSISTANT API (role-based chat flows)
// =========================

export const assistantAPI = {
  // Employee flows
  getLatestPayslip: (month, year) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    const qs = params.toString();
    return apiRequest(`/api/assistant/payslip/latest${qs ? '?' + qs : ''}`);
  },
  getPayslipBreakdown: (month, year) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    const qs = params.toString();
    return apiRequest(`/api/assistant/payslip/breakdown${qs ? '?' + qs : ''}`);
  },
  comparePayslips: () => apiRequest('/api/assistant/payslip/compare'),
  getPayslipPdfLink: (month, year) => {
    const params = new URLSearchParams();
    params.append('month', month);
    params.append('year', year);
    return apiRequest(`/api/assistant/payslip/pdf?${params.toString()}`);
  },
  getLeaveBalance: () => apiRequest('/api/assistant/leave/balance'),
  applyLeave: (data) =>
    apiRequest('/api/assistant/leave/apply', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  getProfile: () => apiRequest('/api/assistant/profile'),
  getForm16Years: () => apiRequest('/api/assistant/form16/years'),

  // Manager flows
  getPendingLeaves: () => apiRequest('/api/assistant/leave/pending'),
  handleLeaveAction: (leaveId, action) =>
    apiRequest('/api/assistant/leave/action', {
      method: 'POST',
      body: JSON.stringify({ leave_id: leaveId, action }),
    }),
  getTodayAttendance: () => apiRequest('/api/assistant/attendance/today'),
  getAlerts: () => apiRequest('/api/assistant/alerts'),

  // HR flows
  getOrganizations: () => apiRequest('/api/assistant/organizations'),
  getPayrollSummary: (month, year, establishmentId) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    if (establishmentId) params.append('establishment_id', establishmentId);
    const qs = params.toString();
    return apiRequest(`/api/assistant/payroll/summary${qs ? '?' + qs : ''}`);
  },
  getEmployeesSummary: (establishmentId) => {
    const qs = establishmentId ? `?establishment_id=${establishmentId}` : '';
    return apiRequest(`/api/assistant/employees/summary${qs}`);
  },
  getComplianceDeadlines: () => apiRequest('/api/assistant/compliance/deadlines'),
  getDepartmentCost: (month, year, establishmentId) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    if (establishmentId) params.append('establishment_id', establishmentId);
    const qs = params.toString();
    return apiRequest(`/api/assistant/department/cost${qs ? '?' + qs : ''}`);
  },
  getComplianceForms: (month, year, establishmentId) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    if (establishmentId) params.append('establishment_id', establishmentId);
    const qs = params.toString();
    return apiRequest(`/api/assistant/compliance/forms${qs ? '?' + qs : ''}`);
  },
  getEmployeeSalaryReport: (month, year, establishmentId) => {
    const params = new URLSearchParams();
    if (month) params.append('month', month);
    if (year) params.append('year', year);
    if (establishmentId) params.append('establishment_id', establishmentId);
    const qs = params.toString();
    return apiRequest(`/api/assistant/employees/salary${qs ? '?' + qs : ''}`);
  },
  getOrganizationSettings: (establishmentId) => {
    const qs = establishmentId ? `?establishment_id=${establishmentId}` : '';
    return apiRequest(`/api/assistant/organization/settings${qs}`);
  },
  askAI: (question, context = {}, history = []) =>
    apiRequest('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ question, context, history }),
    }),
};

export const helpdeskAPI = {
  getEstablishments: () => apiRequest('/api/ai/helpdesk/establishments'),
  getMenu: () => apiRequest('/api/ai/helpdesk/menu'),
  getEmployees: (establishmentId) => apiRequest(`/api/ai/helpdesk/employees/${establishmentId}`),
  executeAction: (actionId, establishmentId, params = {}) =>
    apiRequest('/api/ai/helpdesk/action', {
      method: 'POST',
      body: JSON.stringify({ action_id: actionId, establishment_id: establishmentId, params }),
    }),
};

export default {
  employeeAPI,
  establishmentAPI,
  leaveAPI,
  resignationAPI,
  analyticsAPI,
  leaveConfigAPI,
  wagesAPI,
  attendanceAPI,
  contractorAPI,
  reportsAPI,
  payrollAPI,
  formsAPI,
  invoiceAPI,
  assetAPI,
  documentAPI,
  usersAPI,
  barcodeAPI,
  groupsAPI,
  assistantAPI,
  helpdeskAPI,
};
